// src/pages/accounts/ExpenseLedger.js
// v3 — fixes: removed orderBy (composite index), transport type added

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

const CATEGORY_LABELS = {
  travel:         "Travel / Fuel",
  food:           "Food / Meals",
  labour:         "Labour / Helper",
  misc:           "Miscellaneous",
  expense:        "Expense",
  local_purchase: "Local Purchase",
  transport:      "Transport",
};

const PAYMENT_MODE_LABELS = {
  cash:          "Cash",
  upi:           "UPI",
  bank_transfer: "Bank Transfer",
  cheque:        "Cheque",
};

const STATUS_UI = {
  payment_pending: { label: "Payment Pending", bg: "#fef3c7", color: "#92400e" },
  paid:            { label: "Paid",            bg: "#d1fae5", color: "#065f46" },
};

export default function ExpenseLedger() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || localStorage.getItem("company_id") || "";

  const [expenses,       setExpenses]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");

  useEffect(() => { loadExpenses(); }, [companyId]);

  const loadExpenses = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // ✅ No orderBy — avoids composite index, sort client-side
      // ✅ Fetch both expense + transport types
      const [expSnap, transSnap] = await Promise.all([
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("type",       "==", "expense"))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("type",       "==", "transport"))),
      ]);

      const list = [
        ...expSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        ...transSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      ].sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));

      setExpenses(list);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const filtered = expenses.filter(item => {
    if (categoryFilter && item.category !== categoryFilter && item.type !== categoryFilter) return false;
    if (statusFilter   && item.status   !== statusFilter)   return false;
    return true;
  });

  const totalFiltered = filtered.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPending  = expenses.filter(i => i.status === "payment_pending").reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPaid     = expenses.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.amount || 0), 0);

  const categories = [...new Set(expenses.map(i => i.category || i.type).filter(Boolean))];

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-xl font-bold text-slate-800 mb-5">Expense Ledger</h1>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-red-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Total</p>
          <p className="text-xl font-black mt-0.5">₹{expenses.reduce((s,i) => s + Number(i.amount||0), 0).toLocaleString()}</p>
        </div>
        <div className="bg-amber-500 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Pending Payment</p>
          <p className="text-xl font-black mt-0.5">₹{totalPending.toLocaleString()}</p>
        </div>
        <div className="bg-green-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Paid</p>
          <p className="text-xl font-black mt-0.5">₹{totalPaid.toLocaleString()}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
          ))}
        </select>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Status</option>
          <option value="payment_pending">Payment Pending</option>
          <option value="paid">Paid</option>
        </select>

        <div className="ml-auto text-sm font-semibold text-slate-600 flex items-center">
          Showing: ₹{totalFiltered.toLocaleString()}
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 text-sm text-center mt-10">No records found</p>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">By</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Bill</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const statusUI = STATUS_UI[item.status] || { label: item.status || "-", bg: "#f3f4f6", color: "#374151" };
                  return (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {item.created_at?.seconds
                          ? new Date(item.created_at.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                          : "-"}
                      </td>
                      <td className="p-3 text-slate-600">{item.requested_by_name || item.created_by_name || "-"}</td>
                      <td className="p-3 text-slate-600">{CATEGORY_LABELS[item.category] || CATEGORY_LABELS[item.type] || item.category || item.type || "-"}</td>
                      <td className="p-3 text-slate-600 max-w-[200px] truncate">{item.description || "-"}</td>
                      <td className="p-3 text-slate-500">{PAYMENT_MODE_LABELS[item.payment_mode] || item.payment_mode || "-"}</td>
                      <td className="p-3 text-right font-semibold text-red-600">₹{Number(item.amount || 0).toLocaleString()}</td>
                      <td className="p-3">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ background: statusUI.bg, color: statusUI.color }}>
                          {statusUI.label}
                        </span>
                      </td>
                      <td className="p-3">
                        {item.bill_url
                          ? <a href={item.bill_url} target="_blank" rel="noreferrer"
                              className="text-xs text-blue-500 underline">View</a>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}